import { useState } from "react";
import { FileText, Loader2, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useDeleteFile, useDownloadLink, useRetryFile } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBaseFile } from "@/services/knowledge-base.service";
import { formatOf } from "@/utils/kb-file-types";
import { isEditableText } from "@/utils/kb-text";
import { formatBytes, retryRefusal } from "@/utils/kb-upload-rules";
import { FileBulkBar } from "./FileBulkBar";
import { PreviewDialog } from "./PreviewDialog";

interface FilesTableProps {
  storeId: string;
  workspaceId: string;
  files: KnowledgeBaseFile[];
  /** Percent sent of each file this browser is uploading; null once settled. */
  progress: Record<string, number | null>;
  /** Owner, admin, developer: Retry, Cancel, Preview, Download, and ticking files. */
  canEdit: boolean;
  /** Owner, admin: Delete and Replace. */
  canDelete: boolean;
  /** The plan's files per knowledge base, once the usage has loaded: a retry over it is refused here. */
  maxFiles: number | undefined;
  onUpload: () => void;
  /** Replace on a ready file: the upload, for the file that takes its place. */
  onReplace: (file: KnowledgeBaseFile) => void;
  /** Edit on a ready text file: the text dialog, opened with its text, saved in its place. */
  onEdit: (file: KnowledgeBaseFile) => void;
  /** A file is gone: the description is rewritten from the others. */
  onDeleted: () => void;
}

const TRY_AGAIN = "Try again in a moment.";

function Status({ file, percent }: { file: KnowledgeBaseFile; percent: number | null | undefined }) {
  switch (file.status) {
    case "uploading":
      return typeof percent === "number" ? (
        <div className="w-32 space-y-1">
          <span>Uploading {percent}%</span>
          <Progress value={percent} aria-label={`${file.display_name} uploaded`} className="h-2" />
        </div>
      ) : (
        <span>Uploading</span>
      );
    case "processing":
      // Its passages are counted once the file is read; the percent is of those saved.
      return (
        <span className="flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          {file.chunk_count === 0
            ? "Reading the file…"
            : `Processing · ${Math.min(99, Math.floor((file.chunks_saved * 100) / file.chunk_count))}%`}
        </span>
      );
    case "ready":
      return file.warnings?.length ? (
        <div className="space-y-1" data-testid="kb-file-warnings">
          <Badge variant="secondary">Ready</Badge>
          <p className="text-xs text-muted-foreground">Some parts could not be read:</p>
          <ul className="list-disc pl-4 text-xs text-muted-foreground">
            {file.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : (
        <Badge variant="secondary">Ready</Badge>
      );
    default:
      return (
        <div className="space-y-1">
          <Badge variant="destructive">Failed</Badge>
          {file.error_message && <p className="text-xs text-destructive">{file.error_message}</p>}
        </div>
      );
  }
}

/**
 * A knowledge base's files (design §5, step 3 §5.5), with Preview and Download, Replace on a ready file (and Edit
 * on a ready text file, which replaces it with the edited text), Retry (a failed file), Cancel (an unfinished
 * upload) and Delete, and the same for several ticked at once.
 */
export function FilesTable({
  storeId,
  workspaceId,
  files,
  progress,
  canEdit,
  canDelete,
  maxFiles,
  onUpload,
  onReplace,
  onEdit,
  onDeleted,
}: FilesTableProps) {
  const retry = useRetryFile(storeId, workspaceId);
  const remove = useDeleteFile(storeId, workspaceId);
  const download = useDownloadLink();
  const { toast } = useToast();
  const [deleting, setDeleting] = useState<KnowledgeBaseFile | null>(null);
  const [deleteError, setDeleteError] = useState<string>();
  const [previewing, setPreviewing] = useState<KnowledgeBaseFile | null>(null);
  /** The ticked files' ids: one no longer in the list is no longer ticked. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const ticked = files.filter((file) => selected.has(file.id));
  const nameOf = new Map(files.map((file) => [file.id, file.display_name]));
  /** Files with a replacement on its way: one at a time. */
  const replacing = new Set(files.map((file) => file.replaces_id));

  const failed = (title: string) => (error: unknown) =>
    toast({ variant: "destructive", title, description: failureMessage(error, TRY_AGAIN) });

  const toggle = (id: string) =>
    setSelected((all) => {
      const next = new Set(all);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** A failed file runs again only if its bytes are not another file's and there is room for it among the files
   * (kb_confirm_upload's retry). */
  const retryFile = (file: KnowledgeBaseFile) => {
    const refused = maxFiles === undefined ? null : retryRefusal(file, files, maxFiles);
    if (refused) {
      toast({ variant: "destructive", title: "The file was not queued again", description: refused });
      return;
    }
    retry.mutate(file.id, { onError: failed("The file was not queued again") });
  };

  // The link answers as an attachment: the browser saves the file, and the page stays.
  const downloadFile = (file: KnowledgeBaseFile) =>
    download.mutate(file.id, {
      onSuccess: ({ url }) => window.open(url, "_self"),
      onError: failed("The file was not downloaded"),
    });

  const confirmDelete = async () => {
    setDeleteError(undefined);
    try {
      await remove.mutateAsync(deleting!.id);
      toast({ title: "File deleted", description: `"${deleting!.display_name}" was removed from this knowledge base.` });
      setDeleting(null);
      onDeleted();
    } catch (error) {
      setDeleteError(failureMessage(error, `The file could not be deleted. ${TRY_AGAIN}`));
    }
  };

  if (files.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed py-12 text-center">
        <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <p className="mb-4 text-muted-foreground">No files yet. Upload the documents your agents should know.</p>
        {canEdit && (
          <Button size="sm" onClick={onUpload}>
            <Upload className="mr-2 h-4 w-4" />
            Upload files
          </Button>
        )}
      </div>
    );
  }

  const busy = (mutation: { isPending: boolean; variables?: unknown }, file: KnowledgeBaseFile) =>
    mutation.isPending && mutation.variables === file.id;

  return (
    <>
      {canEdit && (
        <FileBulkBar
          storeId={storeId}
          workspaceId={workspaceId}
          files={files}
          ticked={ticked}
          canDelete={canDelete}
          maxFiles={maxFiles}
          onDone={() => setSelected(new Set())}
          onDeleted={onDeleted}
        />
      )}

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all files"
                    checked={ticked.length === 0 ? false : ticked.length === files.length || "indeterminate"}
                    onCheckedChange={(on) => setSelected(new Set(on === true ? files.map((file) => file.id) : []))}
                  />
                </TableHead>
              )}
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id} data-testid="kb-file" data-file-name={file.display_name}>
                {canEdit && (
                  <TableCell>
                    <Checkbox
                      aria-label={`Select ${file.display_name}`}
                      checked={selected.has(file.id)}
                      onCheckedChange={() => toggle(file.id)}
                    />
                  </TableCell>
                )}
                <TableCell className="max-w-[280px] font-medium">
                  <p className="truncate" title={file.display_name}>
                    {file.display_name}
                  </p>
                  {nameOf.has(file.replaces_id) && (
                    <p className="truncate text-xs font-normal text-muted-foreground">
                      Replaces {nameOf.get(file.replaces_id)}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{formatOf(file.file_type)}</TableCell>
                <TableCell className="text-muted-foreground">{formatBytes(file.file_size)}</TableCell>
                <TableCell>
                  <Status file={file} percent={progress[file.id]} />
                </TableCell>
                <TableCell className="space-x-1 whitespace-nowrap text-right">
                  {canEdit && file.status === "ready" && (
                    <Button variant="ghost" size="sm" onClick={() => setPreviewing(file)}>
                      Preview
                    </Button>
                  )}
                  {canEdit && file.status !== "uploading" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={download.isPending}
                      aria-busy={busy(download, file)}
                      onClick={() => downloadFile(file)}
                    >
                      {busy(download, file) && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      Download
                    </Button>
                  )}
                  {canDelete && isEditableText(file) && !replacing.has(file.id) && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(file)}>
                      Edit
                    </Button>
                  )}
                  {canDelete && file.status === "ready" && !replacing.has(file.id) && (
                    <Button variant="ghost" size="sm" onClick={() => onReplace(file)}>
                      Replace
                    </Button>
                  )}
                  {canEdit && file.status === "error" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={retry.isPending}
                      aria-busy={busy(retry, file)}
                      onClick={() => retryFile(file)}
                    >
                      {busy(retry, file) && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      {busy(retry, file) ? "Retrying…" : "Retry"}
                    </Button>
                  )}
                  {canEdit && file.status === "uploading" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={remove.isPending}
                      aria-busy={busy(remove, file)}
                      onClick={() =>
                        remove.mutate(file.id, {
                          onSuccess: () => toast({ title: "Upload cancelled" }),
                          onError: failed("The upload was not cancelled"),
                        })
                      }
                    >
                      {busy(remove, file) && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      {busy(remove, file) ? "Cancelling…" : "Cancel"}
                    </Button>
                  )}
                  {canDelete && file.status !== "uploading" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      disabled={remove.isPending}
                      onClick={() => {
                        setDeleteError(undefined);
                        setDeleting(file);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {previewing && <PreviewDialog file={previewing} onClose={() => setPreviewing(null)} />}

      <AlertDialog open={!!deleting} onOpenChange={() => !remove.isPending && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The file and its text are removed from this knowledge base. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending} aria-busy={remove.isPending}>
              {remove.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
